import { useCallback, useState } from 'react';

import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';

type ContentTestAssetKind = 'image' | 'video';

interface UploadedAsset {
  assetId: string;
  kind: ContentTestAssetKind;
}

interface UseContentTestAssetUploadResult {
  upload: (file: File) => Promise<UploadedAsset>;
  isUploading: boolean;
}

const inferKind = (mimeType: string): ContentTestAssetKind | null => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return null;
};

export function useContentTestAssetUpload(): UseContentTestAssetUploadResult {
  const [isUploading, setIsUploading] = useState(false);

  const upload = useCallback(async (file: File): Promise<UploadedAsset> => {
    const kind = inferKind(file.type);
    if (!kind) {
      throw new Error('File must be an image or video');
    }
    setIsUploading(true);
    try {
      const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name);

      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);

      await uploadFileToS3(url, formData);
      return { assetId, kind };
    } finally {
      setIsUploading(false);
    }
  }, []);

  return { upload, isUploading };
}
